import type { User } from '../hooks/useAuth';

export type RoleName = 'Admin' | 'Dentist' | 'Receptionist' | 'Assistant';

const ALL_ROLES: RoleName[] = ['Admin', 'Dentist', 'Receptionist', 'Assistant'];

/** Tailwind classes for role-colored tags (shared by Team list and Profile). */
export const ROLE_COLORS: Record<RoleName, string> = {
  Admin: 'bg-red-500/10 text-red-600',
  Dentist: 'bg-blue-500/10 text-blue-600',
  Receptionist: 'bg-green-500/10 text-green-600',
  Assistant: 'bg-purple-500/10 text-purple-600',
};

/** Resolve the color classes for any role string (case-insensitive). */
export function getRoleColor(role: string | null | undefined): string {
  if (!role) return '';
  const normalized = role.toLowerCase();
  const match = ALL_ROLES.find((r) => r.toLowerCase() === normalized);
  return match ? ROLE_COLORS[match] : '';
}

/**
 * Resolve the effective role for a user. Superusers and staff are treated as
 * Admin. Falls back to the role returned by /api/me/.
 */
export function getRole(user: User | null | undefined): RoleName | null {
  if (!user) return null;
  if (user.isSuperuser || user.isStaff) return 'Admin';
  if (user.role) {
    const normalized = user.role.toLowerCase();
    const match = ALL_ROLES.find((r) => r.toLowerCase() === normalized);
    if (match) return match;
  }
  return null;
}

/**
 * True if the user holds one of the allowed roles. Admins are always allowed
 * (unless the roles list explicitly excludes them, which we never do).
 */
export function hasRole(
  user: User | null | undefined,
  roles: RoleName[]
): boolean {
  const role = getRole(user);
  if (!role) return false;
  if (role === 'Admin') return true;
  return roles.includes(role);
}
